export type ForeignDomEditReport = (
  error: Error,
  context: Record<string, string | boolean>,
) => void

/**
 * Makes `removeChild` and `insertBefore` survive a DOM that something outside
 * React has rearranged, instead of throwing NotFoundError from React's commit
 * and replacing the whole app with the error screen.
 *
 * A page translator (Chrome / Edge Translate) swaps each text node React
 * rendered for a `<font>` of its own, and some extensions do the same. React
 * still holds the original, now-detached node, so the next commit that removes
 * it or inserts before it throws (facebook/react#11538, unfixed in React 19).
 *
 * - `removeChild` of a node that is not a child does nothing: the node is
 *   already gone from the parent, which is what the caller wanted.
 * - `insertBefore` with a reference node that is not a child appends instead,
 *   so the new content still appears, possibly out of order, rather than
 *   React believing it is on screen when it is not.
 *
 * Each method reports its first mismatch per page load through `report` and
 * `console.warn`, so a bug of our own that hits this path stays visible.
 * `translated` says whether Chrome Translate had marked the page, which tells
 * a translator from an extension or our own code. Must run before the first
 * render. Returns a function that restores the original methods.
 */
export function tolerateForeignDomEdits(report: ForeignDomEditReport) {
  const proto = Node.prototype
  const { removeChild, insertBefore } = proto
  const reported = new Set<string>()

  const reportOnce = (method: string, parent: Node, node: Node) => {
    if (reported.has(method)) return
    reported.add(method)
    const error = new Error(
      `Ignored ${method} on a node that is not a child of its parent`,
    )
    error.name = 'ForeignDomEdit'
    const context = {
      method,
      parentNodeName: parent.nodeName,
      nodeName: node.nodeName,
      detached: node.parentNode === null,
      translated: /\btranslated-(ltr|rtl)\b/.test(
        document.documentElement.className,
      ),
    }
    console.warn(error.message, context)
    report(error, context)
  }

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportOnce('removeChild', this, child)
      return child
    }
    return removeChild.call(this, child) as T
  }

  proto.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child && child.parentNode !== this) {
      reportOnce('insertBefore', this, child)
      return insertBefore.call(this, node, null) as T
    }
    return insertBefore.call(this, node, child) as T
  }

  return () => {
    proto.removeChild = removeChild
    proto.insertBefore = insertBefore
  }
}
